// frontend/src/components/client/FuelRequestDrawer.jsx

import React, { useState, useEffect } from 'react';
import {
  X, Fuel, Calendar, MapPin, CheckCircle, Clock, AlertCircle,
  Send, Plane, FileText, Eye, ExternalLink, Cloud, ListChecks,
  Calculator, Users, Navigation, ArrowRight, Shield, Wifi, Download
} from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import FuelRequirementCalculator from './FuelRequirementCalculator';
import FlightPlanPDF from './FlightPlanPDF';

const StatusTimeline = ({ status, requestedQuantity, approvedQuantity, completedAt }) => {
  const steps = [
    { key: 'pending', label: 'Requested', icon: Send },
    { key: 'approved', label: 'Approved', icon: CheckCircle },
    { key: 'completed', label: 'Fueled', icon: Fuel }
  ];
  
  const currentIndex = steps.findIndex(s => s.key === status);
  
  return (
    <div className="relative">
      <div className="flex justify-between mb-2">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          const isComplete = idx <= currentIndex;
          const isCurrent = idx === currentIndex;
          return (
            <div key={step.key} className="flex flex-col items-center flex-1">
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center
                ${isComplete ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-500'}
                ${isCurrent ? 'ring-2 ring-green-500' : ''}
              `}>
                <Icon className="w-4 h-4" />
              </div>
              <span className="text-xs text-gray-400 mt-1">{step.label}</span>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-3">
        <span>{requestedQuantity} gal requested</span>
        {approvedQuantity && <span>{approvedQuantity} gal approved</span>}
        {completedAt && <span>Completed: {new Date(completedAt).toLocaleDateString()}</span>}
      </div>
    </div>
  );
};

const SkyVectorLinks = ({ departureIcao, destinationIcao }) => {
  const [showMap, setShowMap] = useState(false);
  
  if (!destinationIcao) {
    return (
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-center">
        <p className="text-yellow-400 text-sm">No destination airport selected</p>
        <p className="text-xs text-gray-400 mt-1">Add a destination to enable flight planning</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      <div className="bg-gray-800/30 rounded-lg p-3">
        <div className="flex items-center justify-between">
          <div className="text-center flex-1">
            <p className="text-xs text-gray-500">Departure</p>
            <p className="text-white font-mono font-bold">{departureIcao || '—'}</p>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-500" />
          <div className="text-center flex-1">
            <p className="text-xs text-gray-500">Destination</p>
            <p className="text-white font-mono font-bold">{destinationIcao}</p>
          </div>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-2">
        <a
          href={`https://skyvector.com/airport/${destinationIcao}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 text-xs transition"
        >
          <Eye className="w-3.5 h-3.5" />
          View {destinationIcao} on SkyVector
        </a>
        
        {departureIcao && (
          <a
            href={`https://skyvector.com/?fpl=${departureIcao}%20${destinationIcao}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 text-xs transition"
          >
            <Navigation className="w-3.5 h-3.5" />
            Plan Route {departureIcao} → {destinationIcao}
          </a>
        )}
        
        <a
          href={`https://www.checkwx.com/weather/${destinationIcao}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg text-cyan-400 text-xs transition"
        >
          <Cloud className="w-3.5 h-3.5" />
          METAR/TAF (CheckWX)
        </a>
        
        <a
          href={`https://notams.aim.faa.gov/notamSearch/nsapp.html#/search?search=${destinationIcao}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 text-xs transition"
        >
          <AlertCircle className="w-3.5 h-3.5" />
          NOTAMs
        </a>
      </div>
      
      <button
        onClick={() => setShowMap(!showMap)}
        className="text-xs text-gray-400 hover:text-white transition flex items-center gap-1"
      >
        <Wifi className="w-3 h-3" />
        {showMap ? 'Hide' : 'Show'} SkyVector Map (new tab recommended)
      </button>
      
      {showMap && (
        <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-xs text-yellow-400">
          ⚠️ For best experience on tablet/cockpit, open SkyVector links in a new tab.
        </div>
      )}
    </div>
  );
};

const FAAChecklist = ({ requestId, initialItems, onUpdate }) => {
  const [items, setItems] = useState(initialItems || {
    preflight_inspection_complete: false,
    documents_on_board: false,
    notam_checked: false,
    weather_briefing_complete: false,
    weight_and_balance_computed: false,
    fuel_requirements_computed: false,
    alternate_airport_identified: false,
    ifr_clearance_obtained: false,
    flight_plan_filed: false
  });
  const [saving, setSaving] = useState(false);
  
  const checklistLabels = {
    preflight_inspection_complete: 'Preflight inspection complete (14 CFR §91.103)',
    documents_on_board: 'Documents on board (AROW: Airworthiness, Registration, Operating limits, Weight & Balance)',
    notam_checked: 'NOTAMs checked',
    weather_briefing_complete: 'Weather briefing obtained (FAA approved source)',
    weight_and_balance_computed: 'Weight & balance computed within limits',
    fuel_requirements_computed: 'Fuel requirements computed (FAA §91.151/§91.169)',
    alternate_airport_identified: 'Alternate airport identified (if required)',
    ifr_clearance_obtained: 'IFR clearance obtained (if applicable)',
    flight_plan_filed: 'Flight plan filed (VFR/IFR)'
  };
  
  const handleToggle = async (key) => {
    const newItems = { ...items, [key]: !items[key] };
    setItems(newItems);
    
    setSaving(true);
    try {
      const token = localStorage.getItem('client_access_token');
      await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/client/fuel/${requestId}/checklist`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ checklist_items: newItems })
        }
      );
      if (onUpdate) onUpdate(newItems);
    } catch (err) {
      console.error('Error saving checklist:', err);
    } finally {
      setSaving(false);
    }
  };
  
  const completedCount = Object.values(items).filter(v => v === true).length;
  const totalCount = Object.keys(items).length;
  const progressPercent = (completedCount / totalCount) * 100;
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-green-400" />
          <span className="text-sm text-white font-medium">FAA Required Checks</span>
        </div>
        <div className="text-xs text-gray-400">
          {completedCount}/{totalCount} complete
        </div>
      </div>
      
      <div className="w-full bg-gray-700 rounded-full h-1.5">
        <div 
          className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {Object.entries(checklistLabels).map(([key, label]) => (
          <label key={key} className="flex items-start gap-2 p-2 hover:bg-gray-700/30 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={items[key] || false}
              onChange={() => handleToggle(key)}
              className="mt-0.5 rounded border-gray-600 text-green-500 focus:ring-green-500"
            />
            <span className="text-xs text-gray-300 flex-1">{label}</span>
          </label>
        ))}
      </div>
      
      {saving && (
        <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
          <div className="w-3 h-3 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
          Saving...
        </div>
      )}
    </div>
  );
};

const FuelRequestDrawer = ({ request, aircraft, onClose, onRefresh }) => {
  const [flightPlan, setFlightPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('fuel');
  const [departureIcao, setDepartureIcao] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('aeroTrick');
  const [tripDistance, setTripDistance] = useState(null);
  const [isNightFlight, setIsNightFlight] = useState(false);
  const [isIFR, setIsIFR] = useState(false);
  const [windResult, setWindResult] = useState(null);
  const [calcResult, setCalcResult] = useState(null);

  // Récupérer la home base depuis l'API  
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const token = localStorage.getItem('client_access_token');
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/company/settings`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.logo_url) setCompanyLogo(data.logo_url);
          if (data.name) setCompanyName(data.name);
          if (data.icao_code) setDepartureIcao(data.icao_code);
        }
      } catch (err) {
        console.error('Error fetching company info:', err);
      }
    };
    fetchCompanyInfo();
  }, []);
  
  useEffect(() => {
    loadFlightPlan();
  }, [request.id]);
  
  const loadFlightPlan = async () => {
    try {
      const token = localStorage.getItem('client_access_token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/client/fuel/${request.id}/flight-plan`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setFlightPlan(data);
      if (data.trip_distance_nm) setTripDistance(data.trip_distance_nm);
    } catch (err) {
      console.error('Error loading flight plan:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'approved': return 'bg-green-500/20 text-green-400';
      case 'completed': return 'bg-blue-500/20 text-blue-400';
      case 'cancelled': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };
  
  const destinationIcao = request.destination_icao || null;
  
  // Données PDF — calcResult (live) prend priorité sur flightPlan (DB)
  const activeCalc = calcResult || null;
  const pdfData = {
    departure_icao: departureIcao,
    departure_name: departureIcao,
    destination_icao: destinationIcao,
    destination_name: destinationIcao,
    trip_distance_nm:
      activeCalc?.trip_distance_nm ??
      flightPlan?.trip_distance_nm ??
      tripDistance,
    calculated_trip_fuel_gallons:
      activeCalc?.trip_fuel_gallons_with_wind ??
      activeCalc?.trip_fuel_gallons ??
      flightPlan?.calculated_trip_fuel_gallons,
    calculated_reserve_fuel_gallons:
      activeCalc?.reserve_fuel_gallons ??
      flightPlan?.calculated_reserve_fuel_gallons,
    calculated_alternate_fuel_gallons:
      activeCalc?.alternate_fuel_gallons ??
      flightPlan?.calculated_alternate_fuel_gallons ??
      0,
    calculated_holding_fuel_gallons:
      activeCalc?.holding_fuel_gallons ??
      flightPlan?.calculated_holding_fuel_gallons ??
      0,
    calculated_total_fuel_required_gallons:
      activeCalc?.total_fuel_required_gallons ??
      flightPlan?.calculated_total_fuel_required_gallons,
    passenger_count:
      activeCalc?.passenger_count ??
      flightPlan?.passenger_count ??
      1,
    baggage_weight_lbs:
      activeCalc?.baggage_weight_lbs ??
      flightPlan?.baggage_weight_lbs ??
      0,
    estimated_taxi_fuel_gallons: flightPlan?.estimated_taxi_fuel_gallons ?? 2,
    is_ifr: isIFR,
    is_night_flight: isNightFlight,
    alternate_icao:
      activeCalc?.alternate_icao_used ??
      flightPlan?.alternate_icao,
    alternate_distance_nm:
      activeCalc?.alternate_distance_used_nm ??
      flightPlan?.alternate_distance_nm,
    fuel_notes: flightPlan?.fuel_notes,
  };
  
  const handleCalculationComplete = (data) => {
    // Stocker immédiatement pour le PDF sans attendre le reload DB
    setCalcResult(data);
    if (data.wind_source) {
      setWindResult(data);
    }
    // Reload en background pour persister
    loadFlightPlan();
  };
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex justify-end">
        <div className="w-full max-w-2xl bg-gray-900 h-full p-8 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-gray-400 mt-4">Loading flight plan...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex justify-end">
      <div className="w-full max-w-2xl bg-gray-900 h-full overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Fuel className="w-5 h-5 text-orange-400" />
            <h3 className="text-white font-semibold">Fuel Request #{request.id}</h3>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
              {request.status.toUpperCase()}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-5">
          {/* Request Summary */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500">Aircraft</p>
                <p className="text-white font-medium">{aircraft?.tail_number}</p>
                <p className="text-xs text-gray-400">{aircraft?.manufacturer} {aircraft?.model}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Quantity</p>
                <p className="text-white font-medium">{request.quantity} gallons</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Scheduled Date</p>
                <p className="text-white text-sm flex items-center gap-1">
                  <Calendar className="w-3 h-3 text-gray-400" />
                  {formatDate(request.scheduled_date)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Home Base</p>
                <p className="text-white text-sm font-mono">{departureIcao || '—'}</p>
              </div>
            </div>
            
            {destinationIcao && (
              <div className="mt-3 pt-3 border-t border-gray-700">
                <p className="text-xs text-gray-500">Destination</p>
                <div className="flex items-center gap-2 mt-1">
                  <MapPin className="w-4 h-4 text-orange-400" />
                  <span className="text-white font-mono font-bold">{destinationIcao}</span>
                </div>
              </div>
            )}
            
            {request.description && (
              <div className="mt-3 pt-3 border-t border-gray-700">
                <p className="text-xs text-gray-500">Notes</p>
                <p className="text-sm text-gray-300">{request.description}</p>
              </div>
            )}
          </div>
          
          {/* Section Tabs */}
          <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
            {[
              { id: 'fuel', label: 'Fuel Status', icon: Fuel },
              { id: 'flight', label: 'Flight Plan', icon: Navigation },
              { id: 'checklist', label: 'FAA Checklist', icon: Shield }
            ].map(section => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-medium transition ${
                    activeSection === section.id
                      ? 'bg-blue-500 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {section.label}
                </button>
              );
            })}
          </div>
          
          {/* Section: Fuel Status */}
          {activeSection === 'fuel' && (
            <div className="space-y-4">
              <StatusTimeline 
                status={request.status}
                requestedQuantity={request.quantity}
                approvedQuantity={request.approved_quantity}
                completedAt={request.completed_at}
              />
              
              {aircraft && (
                <FuelRequirementCalculator
                  aircraft={aircraft}
                  requestId={request.id}
                  departureIcao={departureIcao}
                  destinationIcao={destinationIcao}
                  onCalculationComplete={handleCalculationComplete}
                />
              )}
            </div>
          )}
          
          {/* Section: Flight Plan */}
          {activeSection === 'flight' && (
            <div className="space-y-4">
              <SkyVectorLinks 
                departureIcao={departureIcao}
                destinationIcao={destinationIcao}
              />
              
              <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <Cloud className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm text-white font-medium">Weather Resources</span>
                </div>
                <div className="space-y-2 text-xs">
                  <a 
                    href={`https://www.aviationweather.gov/metar/data?ids=${destinationIcao || departureIcao}&format=raw`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300"
                  >
                    <ExternalLink className="w-3 h-3" />
                    METAR/TAF - Aviation Weather Center
                  </a>
                  <a 
                    href={`https://www.windy.com/?${destinationIcao || departureIcao},${destinationIcao ? 'airport' : ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Windy.com - Interactive weather map
                  </a>
                </div>
              </div>
            </div>
          )}
          
          {/* Section: FAA Checklist */}
          {activeSection === 'checklist' && (
            <FAAChecklist 
              requestId={request.id}
              initialItems={flightPlan?.checklist_items}
              onUpdate={(items) => setFlightPlan({ ...flightPlan, checklist_items: items })}
            />
          )}
          
          {/* Disclaimer - FAA Compliance Warning */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs">
                <p className="text-yellow-400 font-medium">Important - FAA Compliance Notice</p>
                <p className="text-gray-300 mt-1">
                  This tool provides <span className="font-medium">planning estimates only</span>. 
                  For FAA-compliant fuel requirements, pilots must:
                </p>
                <ul className="list-disc list-inside mt-1 text-gray-400 space-y-0.5 ml-2">
                  <li>Obtain an official weather briefing from an FAA-approved source</li>
                  <li>Check NOTAMs for the entire route</li>
                  <li>File the flight plan via Leidos (1800wxbrief.com), ForeFlight, or Garmin Pilot</li>
                  <li>Verify weight & balance within aircraft limits</li>
                  <li>Confirm alternate airport fuel requirements for IFR flights (14 CFR §91.169)</li>
                </ul>
                <p className="text-gray-500 mt-2 text-[11px]">
                  Use "Calculate with Wind" button for NOAA winds aloft data. Without wind data, 
                  calculations may underestimate fuel by 15-30%.
                </p>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="pt-2 space-y-3">
            {/* PDF Download Button */}
            <PDFDownloadLink
              document={
                <FlightPlanPDF
                  flightPlan={pdfData}
                  request={request}
                  aircraft={aircraft}
                  checklist={flightPlan?.checklist_items}
                  companyLogo={companyLogo}
                  companyName={companyName}
                  windData={windResult}
                  calculationResult={calcResult}
                />
              }
              fileName={`FlightPlan_${request.id}_${new Date().toISOString().slice(0,19)}.pdf`}
            >
              {({ loading: pdfLoading }) => (
                <button
                  disabled={pdfLoading}
                  className="w-full py-3 bg-blue-500 hover:bg-blue-600 rounded-xl text-white text-sm font-medium transition flex items-center justify-center gap-2"
                >
                  {pdfLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download Flight Plan PDF
                    </>
                  )}
                </button>
              )}
            </PDFDownloadLink>
            
            {/* Cancel Button */}
            {request.status === 'pending' && (
              <button
                onClick={async () => {
                  if (window.confirm('Cancel this fuel request?')) {
                    try {
                      const token = localStorage.getItem('client_access_token');
                      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/client/fuel/requests/${request.id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      onRefresh();
                      onClose();
                    } catch (err) {
                      console.error('Error cancelling:', err);
                    }
                  }
                }}
                className="w-full py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl text-sm font-medium transition"
              >
                Cancel Request
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FuelRequestDrawer;